import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import {
  GoogleIcon,
  AppleIcon,
  KakaoIcon,
  NaverIcon,
  FacebookIcon,
  TwitterIcon,
  GitHubIcon,
  DiscordIcon
} from '../../../assets/icons/social';
import styles from './UserDetail.module.css';
import userStyles from './Users.module.css';
import Timeline from '../../MyPage/Timeline';
import Confirm from '../../Modals/Confirm';

interface Episode {
  episodeNum: number;
  subject: string;
  description: string;
  cardImg: string;
  custom_image_path?: string;
  openYn: boolean;
  completeYn: boolean;
}

interface Recording {
  id: number;
  file_path: string;
  created_at: string;
}

interface Action {
  id: number;
  created_at: string;
  action: number;
  user_id: string;
  project_id: number;
  episode_id: number;
  sesstion_code: string;
}

interface ProjectImage {
  id: number;
  created_at: string;
  file_path: string;
}

interface Video {
  id: number;
  url: string;
  file_size: number;
  duration: number;
  is_project_level: boolean;
  duration_minutes: number;
  file_size_mb: number;
  created_at: string;
  updated_at: string;
}

type BookStatus = 'requested' | 'draft' | 'in_progress' | 'completed' | 'published' | 'archived' | 'error';

// BookStatus 값에 따른 한국어 레이블을 제공하는 객체
const BOOK_STATUS_LABELS: Record<BookStatus, string> = {
  requested: "전자책 생성 요청",
  draft: "전자책 완성",
  in_progress: "전자책 생성중",
  completed: "전자책 생성 완료",
  published: "전자책 출간",
  archived: "전자책 보관",
  error: "전자책 생성 오류"
};

// BookStatus 값으로 한국어 레이블을 가져오는 함수
const getBookStatusLabel = (status: BookStatus): string => {
  return BOOK_STATUS_LABELS[status] || status;
};

interface Book {
  id: number;
  project_id: number;
  status: BookStatus;
  created_at: string;
  updated_at?: string;
}

interface Delivery {
  id: number;
  project_id: number;
  book_id: number;
  delivery_state: string;
  postal_code: string;
  address: string;
  address_detail?: string;
  recipient_name?: string;
  recipient_phone?: string;
  cover_style_id?: string;
  cover_image_url?: string;
  admin_note?: string;
  created_at: string;
  updated_at?: string;
}

interface Project {
  id: number;
  title: string;
  description: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  recordings?: Recording[];
  actions?: Action[];
  images?: ProjectImage[];
  books?: Book[];
  delivery?: Delivery[];
  videos?: Video[];
}

interface Progress {
  total_episodes_count: number;
  complete_episodes_count: number;
  complete_episodes_list: number[];
  status: string;
  message: string;
}

interface UserProfile {
  id: string;
  username: string;
  email: string;
  name: string;
  calledName: string;
  phone_number: string;
  phone_number_verified: boolean;
  agree_terms_of_service: boolean;
  agree_privacy_policy: boolean;
  agree_marketing_information: boolean;
  provider: string;
  groups: string[];
  roles: string[];
  is_admin: boolean;
  is_system: boolean;
  is_manager: boolean;
  is_user: boolean;
  free_addum_days: number;
  enabled: boolean;
}

interface UserHomeInfo {
  project: Project;
  episodes: Episode[];
  progress: Progress;
  user_profile: UserProfile;
  membership?: {
    subscription?: {
      plan?: {
        name?: string;
        start_at?: string;
        expire_at?: string;
        remain_days?: number;
      };
      payment?: {
        payment_number?: string;
        payment_at?: string;
        payment_amount?: number;
        payment_method?: string;
      };
    };
  };
  delivery?: Array<{
    recipient_name?: string;
    postal_code?: string;
    address?: string;
    address_detail?: string;
    recipient_phone?: string;
    tracking_number?: string;
    created_at?: string;
    updated_at?: string;
  }>;
}

// Users.tsx와 동일한 UserInfo 인터페이스 추가
interface UserInfo {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  emailVerified: boolean;
  createdTimestamp?: number;
  attributes?: Record<string, any>;
}

const UserDetail = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserHomeInfo, getUserDetail, toggleUserStatus, deleteUser, rewriteDigitalBook, deleteCameraRecording } = useApi();
  
  const [homeInfo, setHomeInfo] = useState<UserHomeInfo | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 접기/펼치기 상태
  const [recordingsExpanded, setRecordingsExpanded] = useState(false);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [imagesExpanded, setImagesExpanded] = useState(false);

  // 삭제 확인 상태
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchUserDetail = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      // 두 API를 병렬로 호출
      const [homeInfoData, userInfoData] = await Promise.all([
        getUserHomeInfo(userId),
        getUserDetail(userId)
      ]);
      
      setHomeInfo(homeInfoData);
      setUserInfo(userInfoData);
    } catch (err: any) {
      setError(err.message || '사용자 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [userId, getUserHomeInfo, getUserDetail]);

  useEffect(() => {
    fetchUserDetail();
  }, [fetchUserDetail]);

  // 사용자 상태 변경
  const handleToggleStatus = async (currentEnabled: boolean) => {
    if (!userId) return;
    
    try {
      await toggleUserStatus(userId, !currentEnabled);
      
      // 상태 변경 후 사용자 정보 새로고침
      const [homeInfoData, userInfoData] = await Promise.all([
        getUserHomeInfo(userId),
        getUserDetail(userId)
      ]);
      
      setHomeInfo(homeInfoData);
      setUserInfo(userInfoData);
      
      // 성공 메시지 표시
      alert(`사용자 상태가 ${!currentEnabled ? '활성화' : '비활성화'}되었습니다.`);
    } catch (err: any) {
      console.error('toggleUserStatus error:', err);
      setError(err.message || '사용자 상태 변경에 실패했습니다.');
    }
  };

  // 사용자 삭제
  const handleDeleteUser = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async (result: 'Yes' | 'No') => {
    setShowDeleteConfirm(false);
    
    if (result === 'No' || !userId) {
      return;
    }

    try {
      await deleteUser(userId);
      // 삭제 후 사용자 목록으로 이동
      navigate('/admin/users');
    } catch (err: any) {
      setError(err.message || '사용자 삭제에 실패했습니다.');
    }
  };

  // 실물책 주문이 활성화 상태인지 확인
  const hasPhysicalBookOrderActive = (): boolean => {
    if (!project.delivery || project.delivery.length === 0) {
      return false;
    }

    return project?.delivery?.some(delivery => 
      delivery.delivery_state.toUpperCase() !== 'CANCELLED' && 
      delivery.delivery_state.toUpperCase() !== '취소됨'
    );
  };

  // 디지털 북 쓰기 요청
  const handleWriteDigitalBook = async () => {
    if (!userId || !project) {
      alert('사용자 정보나 프로젝트 정보가 없습니다.');
      return;
    }

    try {
      setLoading(true);
      const response = await rewriteDigitalBook(userId, project.id);
      console.log('디지털 북 쓰기 요청 응답:', response);
      alert('디지털 북 쓰기 요청이 완료되었습니다.');
      fetchUserDetail();
      
    } catch (err: any) {
      console.error('디지털 북 쓰기 요청 오류:', err);
      alert('디지털 북 쓰기 요청 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 프로젝트 레벨 영상녹화
  const handleVideoInterview = () => {
    if (!userId || !project) {
      alert('사용자 정보나 프로젝트 정보가 없습니다.');
      return;
    }

    // 프로젝트 레벨 영상녹화 페이지로 이동
    navigate(`/admin/users/${userId}/projects/${project.id}/video-interview`);
  };

  // 영상 삭제 핸들러
  const handleDeleteVideo = async (videoId: number) => {
    if (!project) {
      alert('프로젝트 정보가 없습니다.');
      return;
    }

    const confirmDelete = window.confirm(`영상 인터뷰 #${videoId}를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`);
    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      await deleteCameraRecording(project.id, videoId.toString());
      alert('영상이 성공적으로 삭제되었습니다.');
      // 사용자 정보 새로고침
      fetchUserDetail();
    } catch (err: any) {
      console.error('영상 삭제 오류:', err);
      alert('영상 삭제 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
    } finally {
      setLoading(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 이전 페이지 정보 가져오기
  const fromPage = location.state?.from;
  const fromPageName = location.state?.fromName;

  // 뒤로가기 처리
  const handleGoBack = () => {
    console.log('handleGoBack called');
    console.log('fromPage:', fromPage);
    
    let targetPath: string;
    
    if (fromPage && fromPage !== '/admin/users') {
      // 다른 페이지에서 온 경우 해당 페이지로 이동
      console.log('Navigating to fromPage:', fromPage);
      targetPath = fromPage;
    } else {
      // 사용자 목록에서 온 경우 또는 기본값
      console.log('Navigating to users list:', '/admin/users');
      targetPath = '/admin/users';
    }
    
    try {
      navigate(targetPath);
      console.log('navigate function called successfully');
    } catch (error) {
      console.error('navigate function failed:', error);
      console.log('Falling back to window.location.href');
      window.location.href = targetPath;
    }
  };

  // 뒤로가기 버튼 텍스트 결정
  const getBackButtonText = () => {
    if (fromPage && fromPage !== '/admin/users') {
      return `← ${fromPageName || '이전 페이지'}로 돌아가기`;
    }
    return '← 목록으로 돌아가기';
  };

  // 소셜 프로바이더 아이콘 컴포넌트
  const SocialProviderIcon = ({ provider }: { provider: string }) => {
    if (!provider || provider === '-') {
      return <span className={styles.socialProviderNone}>-</span>;
    }

    const providerLower = provider.toLowerCase();
    
    switch (providerLower) {
      case 'google':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.google}`}>
            <GoogleIcon width={20} height={20} />
            <span>Google</span>
          </div>
        );
      case 'apple':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.apple}`}>
            <AppleIcon width={20} height={20} />
            <span>Apple</span>
          </div>
        );
      case 'kakao':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.kakao}`}>
            <KakaoIcon width={20} height={20} />
            <span>Kakao</span>
          </div>
        );
      case 'naver':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.naver}`}>
            <NaverIcon width={20} height={20} />
            <span>Naver</span>
          </div>
        );
      case 'facebook':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.facebook}`}>
            <FacebookIcon width={20} height={20} />
            <span>Facebook</span>
          </div>
        );
      case 'twitter':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.twitter}`}>
            <TwitterIcon width={20} height={20} />
            <span>Twitter</span>
          </div>
        );
      case 'github':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.github}`}>
            <GitHubIcon width={20} height={20} />
            <span>GitHub</span>
          </div>
        );
      case 'discord':
        return (
          <div className={`${styles.socialProviderIcon} ${styles.discord}`}>
            <DiscordIcon width={20} height={20} />
            <span>Discord</span>
        </div>
        );
      default:
        return (
          <div className={`${styles.socialProviderIcon} ${styles.unknown}`}>
            <span>{provider}</span>
          </div>
        );
    }
  };

  // 진행률 계산
  const calculateProgress = (progress: Progress) => {
    if (progress.total_episodes_count === 0) return 0;
    return Math.round((progress.complete_episodes_count / progress.total_episodes_count) * 100);
  };

  // 상태 배지 색상
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'new';
      case 'in_progress':
        return 'inProgress';
      case 'completed':
        return 'completed';
      default:
        return 'new';
    }
  };

  // 배송 상태 배지 색상
  const getDeliveryStateClass = (state: string) => {
    switch (state.toLowerCase()) {
      case 'pending':
      case '대기중':
        return 'pending';
      case 'processing':
      case '처리중':
        return 'processing';
      case 'shipped':
      case '배송중':
        return 'shipped';
      case 'delivered':
      case '배송완료':
        return 'delivered';
      case 'cancelled':
      case '취소됨':
        return 'cancelled';
      default:
        return 'pending';
    }
  };

  if (loading) {
    return (
      <div className={styles.userDetail}>
        <h1>사용자 상세 정보</h1>
        <div className={styles.loadingSpinner}>로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.userDetail}>
        <h1>사용자 상세 정보</h1>
        <div className={styles.errorMessage}>
          {error}
          <button onClick={handleGoBack} className={styles.btnBack}>
            {getBackButtonText()}
          </button>
        </div>
      </div>
    );
  }

  if (!homeInfo) {
    return (
      <div className={styles.userDetail}>
        <h1>사용자 상세 정보</h1>
        <div className={styles.errorMessage}>
          사용자 정보를 찾을 수 없습니다.
          <button onClick={handleGoBack} className={styles.btnBack}>
            {getBackButtonText()}
          </button>
        </div>
      </div>
    );
  }

  const { project, episodes, progress, user_profile } = homeInfo;

  return (
    <div className={styles.userDetail}>
      <div className={styles.pageTitleRow}>
        <h1>사용자 상세 정보</h1>
        <button onClick={handleGoBack} className={styles.btnBack}>
          {getBackButtonText()}
        </button>
      </div>
      
      <div className={styles.userDetailContent}>
        {/* 사용자 프로필 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.sectionHeader}>
            <h2>사용자 프로필</h2>
            <div className={styles.sectionHeaderActions}>
              <button
                className={`${userStyles.btnToggleStatus} ${userInfo?.enabled ? userStyles.btnDeactivate : userStyles.btnActivate}`}
                onClick={() => handleToggleStatus(userInfo?.enabled || false)}
              >
                {userInfo?.enabled ? '비활성화' : '활성화'}
              </button>
              <button
                className={userStyles.btnDelete}
                onClick={handleDeleteUser}
              >
                사용자 삭제
              </button>
            </div>
          </div>

          {/* 이름 정보 카드 */}
          <div className={styles.nameInfoCard}>
            <h3>이름 정보</h3>
            <div className={styles.compactGrid}>
              <div className={styles.infoItem}>
                <label>이름</label>
                <span className={styles.nameInfoValue}>{user_profile.name}</span>
              </div>
              <div className={styles.infoItem}>
                <label>호칭</label>
                <span className={styles.nameInfoValue}>{user_profile.calledName}</span>
              </div>
              <div className={styles.infoItem}>
                <label>이메일</label>
                <span className={styles.nameInfoValue}>{user_profile.email}</span>
              </div>
              <div className={styles.infoItem}>
                <label>전화번호</label>
                <div className={styles.phoneInfo}>
                  <span className={styles.nameInfoValue}>{user_profile.phone_number}</span>
                  <span className={`${styles.verificationBadge} ${user_profile.phone_number_verified ? styles.verified : styles.unverified}`}>
                    {user_profile.phone_number_verified ? '인증됨' : '미인증'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* 계정 정보 카드 */}
          <div className={styles.profileCard}>
            <h3>계정 정보</h3>
            <div className={styles.compactGrid}>
              <div className={styles.infoItem}>
                <label>소셜 로그인</label>
                <SocialProviderIcon provider={user_profile.provider} />
              </div>
              <div className={styles.infoItem}>
                <label>권한</label>
                <div className={styles.badgeContainer}>
                  {user_profile.roles.map((role, index) => (
                    <span key={index} className={`${styles.roleBadge} ${styles[role as keyof typeof styles]}`}>
                      {role}
                    </span>
                  ))}
                </div>
              </div>
              <div className={styles.infoItem}>
                <label>그룹</label>
                <div className={styles.badgeContainer}>
                  {user_profile.groups.map((group, index) => (
                    <span key={index} className={styles.groupBadge}>
                      {group}
                    </span>
                  ))}
                </div>
              </div>
              <div className={styles.infoItem}>
                <label>계정 상태</label>
                <div className={styles.badgeContainer}>
                  <span className={`${styles.statusBadge} ${user_profile.is_admin ? styles.admin : styles.user}`}>
                    {user_profile.is_admin ? '관리자' : '일반사용자'}
                  </span>
                  <span className={`${styles.statusBadge} ${user_profile.is_system ? styles.system : styles.normal}`}>
                    {user_profile.is_system ? '시스템' : '일반'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* 약관 동의 카드 */}
          <div className={styles.profileCard}>
            <h3>약관 동의</h3>
            <div className={styles.agreementGrid}>
              <div className={styles.agreementItem}>
                <span className={styles.agreementLabel}>서비스약관</span>
                <span className={`${styles.agreementBadge} ${user_profile.agree_terms_of_service ? styles.agreed : styles.notAgreed}`}>
                  {user_profile.agree_terms_of_service ? '동의' : '미동의'}
                </span>
              </div>
              <div className={styles.agreementItem}>
                <span className={styles.agreementLabel}>개인정보처리방침</span>
                <span className={`${styles.agreementBadge} ${user_profile.agree_privacy_policy ? styles.agreed : styles.notAgreed}`}>
                  {user_profile.agree_privacy_policy ? '동의' : '미동의'}
                </span>
              </div>
              <div className={styles.agreementItem}>
                <span className={styles.agreementLabel}>마케팅정보</span>
                <span className={`${styles.agreementBadge} ${user_profile.agree_marketing_information ? styles.agreed : styles.notAgreed}`}>
                  {user_profile.agree_marketing_information ? '동의' : '미동의'}
                </span>
              </div>
            </div>
          </div>

          {/* 결제 정보 카드 - payment 정보가 있는 경우 */}
          {homeInfo?.membership?.subscription?.payment && (
            <div className={styles.profileCard}>
              <h3>결제 정보</h3>
              <div className={styles.agreementGrid}>
                <div className={styles.agreementItem}>
                  <span className={styles.agreementLabel}>결제 번호</span>
                  <span className={styles.agreementBadge}>
                    {homeInfo.membership.subscription.payment.payment_number || '-'}
                  </span>
                </div>
                <div className={styles.agreementItem}>
                  <span className={styles.agreementLabel}>결제일</span>
                  <span className={styles.agreementBadge}>
                    {homeInfo.membership.subscription.payment.payment_at || '-'}
                  </span>
                </div>
                <div className={styles.agreementItem}>
                  <span className={styles.agreementLabel}>결제 금액</span>
                  <span className={styles.agreementBadge}>
                    {homeInfo.membership.subscription.payment.payment_amount || '-'} 원
                  </span>
                </div>
                <div className={styles.agreementItem}>
                  <span className={styles.agreementLabel}>결제 수단</span>
                  <span className={styles.agreementBadge}>
                    {homeInfo.membership.subscription.payment.payment_method || '-'}
                  </span>
                </div>
              </div>
            </div>
          )}
          {/* 구독 정보 카드 */}
          {/* <div className={styles.profileCard}>
            <div className={styles.subscriptionHeader}>
              <h3>구독 정보</h3>
              <div className={styles.comingSoonBadge}>
                <div className={styles.comingSoonIcon}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                </div>
                <span>준비 중</span>
              </div>
            </div>
            <div className={styles.comingSoonContent}>
              <div className={styles.comingSoonIllustration}>
                <div className={styles.rocketContainer}>
                  <div className={styles.rocket}>
                    <div className={styles.rocketBody}>
                      <div className={styles.rocketWindow}></div>
                    </div>
                    <div className={styles.rocketFlame}></div>
                  </div>
                </div>
                <div className={styles.stars}>
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className={styles.star} style={{ animationDelay: `${i * 0.2}s` }}></div>
                  ))}
                </div>
              </div>
              <div className={styles.comingSoonText}>
                <h4>곧 만나요! 🚀</h4>
                <p>구독 정보 관리 기능이 곧 출시됩니다.</p>
                <div className={styles.featurePreview}>
                  <div className={styles.featureItem}>
                    <span className={styles.featureIcon}>📊</span>
                    <span>구독 현황 대시보드</span>
                  </div>
                  <div className={styles.featureItem}>
                    <span className={styles.featureIcon}>⏰</span>
                    <span>자동 갱신 관리</span>
                  </div>
                  <div className={styles.featureItem}>
                    <span className={styles.featureIcon}>💳</span>
                    <span>결제 내역 조회</span>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
          {/* 추가 정보 카드 */}
          <div className={styles.profileCard}>
            <h3>추가 정보</h3>
            <div className={styles.compactGrid}>
              <div className={styles.infoItem}>
                <label>무료 추가 일수</label>
                <span className={styles.infoValue}>{user_profile.free_addum_days}일</span>
              </div>
            </div>
          </div>

          {/* 아이디 정보 카드 */}
          <div className={styles.profileCard}>
            <h3>아이디 정보</h3>
            <div className={styles.compactGrid}>
              <div className={styles.infoItem}>
                <label>사용자 ID</label>
                <span className={styles.basicInfoValue}>{user_profile.id}</span>
              </div>
              <div className={styles.infoItem}>
                <label>사용자명</label>
                <span className={styles.basicInfoValue}>{user_profile.username}</span>
              </div>
            </div>
          </div>
        </div>

        {/* 프로젝트 정보 섹션 */}
        <div className={styles.userInfoSection}>
          <div className={styles.sectionHeader}>
            <h2>프로젝트 정보</h2>
            <div className={styles.sectionHeaderActions}>
              <button
                onClick={handleVideoInterview}
                className={`${styles.managementButton} ${styles.videoInterviewButton}`}
                title="프로젝트 레벨 영상녹화를 시작합니다"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                프로젝트 레벨 영상녹화
              </button>
            </div>
          </div>
          
          {/* 기본 프로젝트 정보 카드 */}
          <div className={styles.basicInfoCard}>
            <h3>기본 정보</h3>
            <div className={styles.compactGrid}>
              <div className={styles.infoItem}>
                <label>프로젝트 ID</label>
                <span className={styles.basicInfoValue}>{project.id}</span>
              </div>
              <div className={styles.infoItem}>
                <label>생성일</label>
                <span className={styles.basicInfoValue}>{formatDate(project.created_at)}</span>
              </div>
              <div className={styles.infoItem}>
                <label>수정일</label>
                <span className={styles.basicInfoValue}>{formatDate(project.updated_at)}</span>
              </div>
            </div>
          </div>
          
          {/* 진행 상태 카드 - Timeline과 동일 */}
          <div className={styles.profileCard}>
            <h3>진행 상태</h3>
            <div className={styles.progressStatusDisplay}>
              <span className={`${styles.progressStatusBadge} ${styles[getStatusBadgeClass(progress.status) as keyof typeof styles]}`}>
                {progress.message}
              </span>
            </div>
            {/* Timeline 컴포넌트 추가 */}
            <div className={styles.timelineContainer}>
              <Timeline currentStatus={progress.message} />
            </div>

            {/* 전자책 정보 카드 */}
            {project.books && project.books.length > 0 && (
              <div className={styles.profileCard}>
                  <div className="flex justify-between items-center mb-4">
                    <h3>전자책 정보 ({project.books.length}개)</h3>
                  </div>
                  <div className={styles.booksList}>
                  {project.books.map((book) => (
                    <div key={book.id} className={styles.bookItem}>
                      <div className={styles.bookHeader}>
                        <div className={styles.bookInfo}>
                          <span className={styles.bookId}>ID: {book.id}</span>
                          <span className={`${styles.bookStatus} ${styles[book.status as keyof typeof styles]}`}>
                            {getBookStatusLabel(book.status as BookStatus)}
                          </span>
                        </div>
                      </div>
                      <div className={styles.bookDates}>
                        <span className={styles.bookCreated}>생성: {formatDate(book.created_at)}</span>
                        {book.updated_at && (
                          <span className={styles.bookUpdated}>수정: {formatDate(book.updated_at)}</span>
                        )}
                      </div>
                      <div className={styles.bookActions}>
                        <button
                          className={styles.bookEditBtn}
                          onClick={() => navigate(`/admin/users/${userId}/projects/${project.id}/books/${book.id}`, {
                            state: {
                              from: `/admin/users/detail/${userId}`,
                              fromName: '사용자 상세정보'
                            }
                          })}
                          title="전자책 상세정보 보기"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                          </svg>
                          상세정보 보기
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 영상 인터뷰 섹션 */}
          <div className={styles.videoInterviewSection}>
            <div className={styles.videoInterviewCard}>
              <div className={styles.videoInterviewContent}>
                <div className={styles.videoInterviewIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className={styles.videoInterviewInfo}>
                  <h3>영상 인터뷰 녹화</h3>
                  <p>카메라를 통해 실시간으로 인터뷰를 녹화할 수 있습니다.</p>
                  <div className={styles.videoInterviewFeatures}>
                    <span className={styles.featureTag}>실시간 녹화</span>
                    <span className={styles.featureTag}>고품질 영상</span>
                    <span className={styles.featureTag}>자동 저장</span>
                  </div>
                </div>
                <div className={styles.videoInterviewActions}>
                  <button 
                    onClick={() => navigate(`/admin/users/${userId}/projects/${project.id}/video-interview`, {
                      state: {
                        from: `/admin/users/detail/${userId}`,
                        fromName: '사용자 상세정보'
                      }
                    })}
                    className={styles.videoInterviewButton}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                    </svg>
                    영상 인터뷰 시작
                  </button>
                </div>
              </div>
            </div>
          </div>

          </div>

        {/* 녹화된 영상 섹션 */}
        {project.videos && project.videos.length > 0 && (
          <div className={styles.userInfoSection}>
            <h2>녹화된 영상</h2>
            <div className={styles.videosGrid}>
              {project.videos.map((video) => (
                <div key={video.id} className={styles.videoCard}>
                  <div className={styles.videoThumbnail}>
                    <video 
                      className={styles.videoPlayer}
                      controls
                      preload="metadata"
                      poster=""
                    >
                      <source src={video.url} type="video/mp4" />
                      브라우저가 비디오를 지원하지 않습니다.
                    </video>
                    <div className={styles.videoDurationBadge}>
                      {Math.round(video.duration)}초
                    </div>
                  </div>
                  <div className={styles.videoInfo}>
                    <div className={styles.videoHeader}>
                      <h4 className={styles.videoTitle}>영상 인터뷰 #{video.id}</h4>
                      <button
                        className={styles.videoDeleteButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVideo(video.id);
                        }}
                        title="영상 삭제"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <div className={styles.videoMeta}>
                      <span className={styles.videoDate}>{formatDate(video.created_at)}</span>
                      <span className={styles.videoSize}>{video.file_size_mb.toFixed(1)}MB</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 실물책 주문 정보 섹션 */}
        {project.delivery && project.delivery.length > 0 && (
          <div className={styles.profileCard}>
            <h3>실물 책 신청 및 배송 정보</h3>
            <div className={styles.deliveryGrid}>
              {project.delivery.map((delivery) => (
                <div key={delivery.id} className={styles.deliveryCard}>
                  <div className={styles.deliveryCardHeader}>
                    <div className={styles.deliveryCardTitle}>
                      <span className={styles.deliveryIcon}>📦</span>
                      <span className={styles.deliveryCardId}>실물책 주문 정보 (#{delivery.id})</span>
                    </div>
                    <div className={styles.deliveryStateContainer}>
                      <span className={`${styles.deliveryStateBadge} ${styles[getDeliveryStateClass(delivery.delivery_state) as keyof typeof styles]}`}>
                        {delivery.delivery_state}
                      </span>
                      <button
                        className={styles.bookEditBtn}
                        onClick={() => navigate(`/admin/delivery/detail/${delivery.id}`, {
                          state: {
                            from: `/admin/users/detail/${userId}`,
                            fromName: '사용자 상세정보'
                          }
                        })}
                        title="실물책 주문 상세정보 보기"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className={styles.deliveryCardContent}>
                    <div className={styles.deliveryInfoSection}>
                      <h4 className={styles.deliverySectionTitle}>
                        <span className={styles.deliverySectionIcon}>📍</span>
                        배송지 정보
                      </h4>
                      <div className={styles.deliveryAddressGrid}>
                        <div className={styles.deliveryAddressItem}>
                          <label>우편번호</label>
                          <span>{delivery.postal_code}</span>
                        </div>
                        <div className={styles.deliveryAddressItem}>
                          <label>주소</label>
                          <span>{delivery.address}</span>
                        </div>
                        {delivery.address_detail && (
                          <div className={styles.deliveryAddressItem}>
                            <label>상세주소</label>
                            <span>{delivery.address_detail}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={styles.deliveryInfoSection}>
                      <h4 className={styles.deliverySectionTitle}>
                        <span className={styles.deliverySectionIcon}>👤</span>
                        수령인 정보
                      </h4>
                      <div className={styles.deliveryRecipientGrid}>
                        {delivery.recipient_name && (
                          <div className={styles.deliveryRecipientItem}>
                            <label>수령인</label>
                            <span>{delivery.recipient_name}</span>
                          </div>
                        )}
                        {delivery.recipient_phone && (
                          <div className={styles.deliveryRecipientItem}>
                            <label>연락처</label>
                            <span>{delivery.recipient_phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className={styles.deliveryInfoSection}>
                      <h4 className={styles.deliverySectionTitle}>
                        <span className={styles.deliverySectionIcon}>🎨</span>
                        책 커스터마이징
                      </h4>
                      <div className={styles.deliveryCustomGrid}>
                        {delivery.cover_style_id && (
                          <div className={styles.deliveryCustomItem}>
                            <label>커버 스타일</label>
                            <span>{delivery.cover_style_id}</span>
                          </div>
                        )}
                        {delivery.cover_image_url && (
                          <div className={styles.deliveryCustomItem}>
                            <label>커버 이미지</label>
                            <span className={styles.deliveryImageUrl}>{delivery.cover_image_url}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {delivery.admin_note && (
                      <div className={styles.deliveryInfoSection}>
                        <h4 className={styles.deliverySectionTitle}>
                          <span className={styles.deliverySectionIcon}>📝</span>
                          관리자 메모
                        </h4>
                        <div className={styles.deliveryAdminNote}>
                          {delivery.admin_note}
                        </div>
                      </div>
                    )}

                    <div className={styles.deliveryInfoSection}>
                      <h4 className={styles.deliverySectionTitle}>
                        <span className={styles.deliverySectionIcon}>📅</span>
                        처리 일정
                      </h4>
                      <div className={styles.deliveryDatesGrid}>
                        <div className={styles.deliveryDateItem}>
                          <label>신청일</label>
                          <span>{formatDate(delivery.created_at)}</span>
                        </div>
                        {delivery.updated_at && (
                          <div className={styles.deliveryDateItem}>
                            <label>수정일</label>
                            <span>{formatDate(delivery.updated_at)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 인터뷰 상태 섹션 */}
        <div className={styles.userInfoSection}>
          <h2>인터뷰 상태</h2>
          {/* 에피소드 진행률 카드 */}
          <div className={styles.progressCard}>
            <h3>에피소드 진행률</h3>
            <div className={styles.progressStats}>
              <div className={styles.progressStat}>
                <span className={styles.statNumber}>{progress.complete_episodes_count}</span>
                <span className={styles.statLabel}>완료된 에피소드</span>
              </div>
              <div className={styles.progressStat}>
                <span className={styles.statNumber}>{progress.total_episodes_count}</span>
                <span className={styles.statLabel}>전체 에피소드</span>
              </div>
              <div className={styles.progressStat}>
                <span className={styles.statNumber}>{calculateProgress(progress)}%</span>
                <span className={styles.statLabel}>진행률</span>
              </div>
            </div>
            <div className={styles.progressText}>
              <span className={styles.progressPercentage}>
                <strong>{progress.total_episodes_count}</strong>개 중에 <strong>{progress.complete_episodes_count}</strong>개 완료
              </span>
            </div>

            {/* 완료된 에피소드 목록 */}
            {progress.complete_episodes_list.length > 0 && (
              <div className={styles.completedEpisodesSection}>
                <h4>완료된 에피소드 목록</h4>
                <div className={styles.completedEpisodesList}>
                  {progress.complete_episodes_list.map((episodeNum) => (
                    <span key={episodeNum} className={styles.completedEpisodeBadge} onClick={() => navigate(`/admin/users/${userId}/projects/${project.id}/episodes/${episodeNum}/interview`)}
                    style={{ cursor: 'pointer' }}>
                      {episodeNum}화
                    </span>
                  ))}
                </div>

                <div className={styles.digitalBookSection}>
                  <button 
                    className={styles.digitalBookButton}
                    onClick={() => handleWriteDigitalBook()}
                    disabled={progress.complete_episodes_list.length === 0 || hasPhysicalBookOrderActive()}
                  >
                    <span className={styles.digitalBookIcon}>📚</span>
                    <span className={styles.digitalBookText}>
                      {progress.complete_episodes_list.length > 0 
                        ? '디지털 북 생성 요청' 
                        : '완료된 에피소드가 없습니다'
                      }
                    </span>
                  </button>
                  {progress.complete_episodes_list.length > 0 && (
                    <p className={styles.digitalBookNote}>
                      완료된 {progress.complete_episodes_list.length}개 에피소드를 기반으로 디지털 북을 생성합니다.
                    </p>
                  )}
                </div>

              </div>
            )}
          </div>

          <div className={styles.episodesGrid}>
            {episodes.map((episode) => (
              <div 
                key={episode.episodeNum} 
                className={`${styles.episodeCard} ${episode.completeYn ? styles.completed : episode.openYn ? styles.open : styles.locked}`}
                onClick={() => navigate(`/admin/users/${userId}/projects/${project.id}/episodes/${episode.episodeNum}/interview`)}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.episodeImage}>
                  <img src={episode.cardImg} alt={episode.subject} />
                  {/* 완료 상태 플래그 - 왼쪽 위 */}
                  {episode.completeYn && (
                    <div className={styles.episodeCompleteFlag}>
                      <span className={styles.completeFlagIcon}>🏆</span>
                    </div>
                  )}
                  {/* 상태 오버레이 - 오른쪽 위 */}
                  <div className={styles.episodeStatusOverlay}>
                    {episode.completeYn ? (
                      <span className={styles.statusComplete}>✓ 완료</span>
                    ) : episode.openYn ? (
                      <span className={styles.statusOpen}>열림</span>
                    ) : (
                      <span className={styles.statusLocked}>🔒 잠김</span>
                    )}
                  </div>
                  {/* 커스텀 이미지 오버레이 - 오른쪽 아래 */}
                  {episode.custom_image_path && (
                    <div className={styles.customImageOverlay}>
                      <img src={episode.custom_image_path} alt="커스텀 이미지" />
                    </div>
                  )}
                </div>
                <div className={styles.episodeInfo}>
                  <h4 className={styles.episodeTitle}>{episode.episodeNum}화: {episode.subject}</h4>
                  <p className={styles.episodeDescription}>{episode.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 녹음 파일 정보 카드 */}
        {project.recordings && project.recordings.length > 0 && (
          <div className={styles.profileCard}>
            <div className={styles.collapsibleHeader} onClick={() => setRecordingsExpanded(!recordingsExpanded)}>
              <h3>녹음 파일 ({project.recordings.length}개)</h3>
              <span className={styles.expandIcon}>{recordingsExpanded ? '▼' : '▶'}</span>
            </div>
            {recordingsExpanded && (
              <div className={styles.recordingsList}>
                {project.recordings.map((recording) => (
                  <div key={recording.id} className={styles.recordingItem}>
                    <span className={styles.recordingId}>ID: {recording.id}</span>
                    <span className={styles.recordingPath}>{recording.file_path}</span>
                    <span className={styles.recordingDate}>{formatDate(recording.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      {/* 액션 정보 카드 */}
      {project.actions && project.actions.length > 0 && (
        <div className={styles.profileCard}>
          <div className={styles.collapsibleHeader} onClick={() => setActionsExpanded(!actionsExpanded)}>
            <h3>사용자 액션 ({project.actions.length}개)</h3>
            <span className={styles.expandIcon}>{actionsExpanded ? '▼' : '▶'}</span>
          </div>
          {actionsExpanded && (
            <div className={styles.actionsList}>
              {project.actions.map((action) => (
                <div key={action.id} className={styles.actionItem}>
                  <span className={styles.actionId}>ID: {action.id}</span>
                  <span className={styles.actionType}>액션: {action.action}</span>
                  <span className={styles.actionEpisode}>에피소드: {action.episode_id}</span>
                  <span className={styles.actionDate}>{formatDate(action.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* 프로젝트 이미지 정보 카드 */}
        {project.images && project.images.length > 0 && (
          <div className={styles.profileCard}>
            <div className={styles.collapsibleHeader} onClick={() => setImagesExpanded(!imagesExpanded)}>
              <h3>프로젝트 이미지 ({project.images.length}개)</h3>
              <span className={styles.expandIcon}>{imagesExpanded ? '▼' : '▶'}</span>
            </div>
            {imagesExpanded && (
              <div className={styles.imagesList}>
                {project.images.map((image) => (
                  <div key={image.id} className={styles.imageItem}>
                    <span className={styles.imageId}>ID: {image.id}</span>
                    <span className={styles.imagePath}>{image.file_path}</span>
                    <span className={styles.imageDate}>{formatDate(image.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 삭제 확인 다이얼로그 */}
      <Confirm
        show={showDeleteConfirm}
        title="사용자 삭제 확인"
        message="정말로 이 사용자를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다."
        onClose={handleDeleteConfirm}
        confirmText="삭제하기"
        cancelText="취소"
      />
    </div>
  );
};

export default UserDetail;
